import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Plus, Trash } from "@phosphor-icons/react";

export const BAND_CLASS = {
  low: "bg-emerald-600 text-white", medium: "bg-amber-500 text-white", high: "bg-orange-600 text-white", very_high: "bg-red-600 text-white",
};

export function ratingOf(l, s) { return Math.max(1, Math.min(5, +l || 1)) * Math.max(1, Math.min(5, +s || 1)); }
export function bandOf(r) { return r >= 16 ? "very_high" : r >= 10 ? "high" : r >= 5 ? "medium" : "low"; }

export function RiskBadge({ l, s, testId }) {
  const r = ratingOf(l, s);
  const b = bandOf(r);
  return <span className={`inline-flex items-center justify-center min-w-[64px] px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${BAND_CLASS[b]}`} data-testid={testId}>{r} {b.replace("_", " ")}</span>;
}

function Score({ value, onChange, options, testId }) {
  return (
    <select value={value} onChange={e => onChange(+e.target.value)} className="h-7 w-full rounded border border-border bg-background text-xs px-1" data-testid={testId}>
      {options.map(o => <option key={o.value} value={o.value}>{o.value} · {o.label}</option>)}
    </select>
  );
}

/** Editable 5x5 risk table: hazard, who, controls, initial L/S and residual L/S with live colour-coded ratings. */
export default function RamsHazardTable({ hazards, onChange, library, readOnly = false }) {
  const L = library?.likelihood || [1, 2, 3, 4, 5].map(v => ({ value: v, label: "" }));
  const S = library?.severity || [1, 2, 3, 4, 5].map(v => ({ value: v, label: "" }));
  const update = (i, patch) => onChange(hazards.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const remove = (i) => onChange(hazards.filter((_, idx) => idx !== i));
  const add = (preset) => onChange([...hazards, { id: Math.random().toString(36).slice(2, 10), hazard: "", who: "Operatives", controls: "", l: 3, s: 3, rl: 2, rs: 3, ...(preset || {}) }]);

  return (
    <div className="space-y-3" data-testid="rams-hazard-table">
      {hazards.length === 0 && <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">No hazards yet — add from the library, use AI draft, or add a blank row.</p>}
      {hazards.map((h, i) => (
        <div key={h.id || i} className="border border-border rounded-lg p-3 bg-card space-y-2" data-testid={`rams-hazard-row-${i}`}>
          <div className="flex items-start gap-2">
            <span className="text-xs font-bold text-muted-foreground mt-2 w-5">{i + 1}</span>
            <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-2">
              <Input value={h.hazard} onChange={e => update(i, { hazard: e.target.value })} placeholder="Hazard" className="h-8 text-xs font-semibold" readOnly={readOnly} data-testid={`rams-hazard-${i}-hazard`} />
              <Input value={h.who} onChange={e => update(i, { who: e.target.value })} placeholder="Who is at risk" className="h-8 text-xs" readOnly={readOnly} data-testid={`rams-hazard-${i}-who`} />
            </div>
            {!readOnly && <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600" onClick={() => remove(i)} data-testid={`rams-hazard-${i}-remove`}><Trash size={14} /></Button>}
          </div>
          <div className="pl-7 grid md:grid-cols-[1fr_260px] gap-2">
            <Textarea value={h.controls} onChange={e => update(i, { controls: e.target.value })} placeholder="Control measures" rows={2} className="text-xs" readOnly={readOnly} data-testid={`rams-hazard-${i}-controls`} />
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
              <div className="col-span-2 flex items-center justify-between"><span className="font-semibold uppercase text-muted-foreground">Initial</span><RiskBadge l={h.l} s={h.s} testId={`rams-hazard-${i}-initial`} /></div>
              <Score value={h.l} onChange={v => update(i, { l: v })} options={L} testId={`rams-hazard-${i}-l`} />
              <Score value={h.s} onChange={v => update(i, { s: v })} options={S} testId={`rams-hazard-${i}-s`} />
              <div className="col-span-2 flex items-center justify-between mt-1"><span className="font-semibold uppercase text-muted-foreground">Residual</span><RiskBadge l={h.rl} s={h.rs} testId={`rams-hazard-${i}-residual`} /></div>
              <Score value={h.rl} onChange={v => update(i, { rl: v })} options={L} testId={`rams-hazard-${i}-rl`} />
              <Score value={h.rs} onChange={v => update(i, { rs: v })} options={S} testId={`rams-hazard-${i}-rs`} />
            </div>
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => add()} data-testid="rams-hazard-add"><Plus size={14} className="mr-1" /> Blank hazard</Button>
          {library?.hazards && (
            <details className="relative">
              <summary className="list-none cursor-pointer text-xs h-8 inline-flex items-center px-3 rounded-md border border-border hover:bg-muted" data-testid="rams-hazard-library-toggle">Add from library ▾</summary>
              <div className="absolute z-20 mt-1 w-[520px] max-w-[90vw] max-h-80 overflow-y-auto bg-card border border-border rounded-md shadow-lg p-2 space-y-2">
                {Object.entries(library.hazards).map(([group, list]) => (
                  <div key={group}>
                    <p className="text-[10px] font-bold uppercase text-muted-foreground px-1 py-1">{group}</p>
                    {list.map((p, k) => (
                      <button key={k} type="button" onClick={() => add({ hazard: p.hazard, who: p.who, controls: p.controls, l: p.l, s: p.s, rl: Math.max(1, p.l - 1), rs: p.s })}
                        className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted" data-testid={`rams-hazard-library-${group.replace(/\W+/g, "-").toLowerCase()}-${k}`}>
                        <span className="font-semibold">{p.hazard}</span><span className="text-muted-foreground"> — {p.controls.slice(0, 70)}…</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
